import { z } from "zod";
import { DefaultOrganizationIdSchema } from "./organization-id.const.ts";

export {
	type DefaultOrganizationId,
	DefaultOrganizationIdSchema,
} from "./organization-id.const.ts";

const identifier = z
	.string()
	.trim()
	.min(1)
	.refine((value) => !value.startsWith("<"), {
		message: "still a placeholder",
	});

// Provider vocabulary for this platform's second-generation callable endpoints.
export const FunctionsRegionSchema = z.enum([
	"africa-south1",
	"asia-east1",
	"asia-northeast1",
	"asia-northeast2",
	"asia-northeast3",
	"asia-south1",
	"asia-south2",
	"asia-southeast1",
	"asia-southeast2",
	"australia-southeast1",
	"australia-southeast2",
	"europe-central2",
	"europe-north1",
	"europe-southwest1",
	"europe-west1",
	"europe-west2",
	"europe-west3",
	"europe-west4",
	"europe-west6",
	"europe-west8",
	"europe-west9",
	"europe-west10",
	"europe-west12",
	"me-central1",
	"me-central2",
	"me-west1",
	"northamerica-northeast1",
	"northamerica-northeast2",
	"southamerica-east1",
	"southamerica-west1",
	"us-central1",
	"us-east1",
	"us-east4",
	"us-east5",
	"us-south1",
	"us-west1",
	"us-west2",
	"us-west3",
	"us-west4",
]);
export type FunctionsRegion = z.infer<typeof FunctionsRegionSchema>;

// Infrastructure only: domain, brand, Maps, payment and credentials belong to
// runtime workspace/settings records, never this build projection.
export const FirebaseOptionsSchema = z
	.object({
		apiKey: identifier,
		authDomain: identifier,
		projectId: identifier,
		storageBucket: identifier,
		messagingSenderId: identifier,
		appId: identifier,
	})
	.strict();
export type FirebaseOptions = z.infer<typeof FirebaseOptionsSchema>;

export const BrowserBootstrapSchema = z
	.object({
		defaultHostingOrigin: z
			.string()
			.url()
			.refine((value) => {
				try {
					const url = new URL(value);
					return (
						url.protocol === "https:" &&
						url.origin === value &&
						!url.username &&
						!url.password
					);
				} catch {
					return false;
				}
			}, "expected a canonical HTTPS Hosting origin"),
		passkeyOrigins: z
			.array(
				z
					.string()
					.url()
					.refine((value) => {
						try {
							const url = new URL(value);
							return (
								url.protocol === "https:" &&
								url.origin === value &&
								!url.username &&
								!url.password
							);
						} catch {
							return false;
						}
					}, "expected a canonical HTTPS origin"),
			)
			.nullish(),
		firebase: FirebaseOptionsSchema,
		appCheck: z.object({ recaptchaSiteKey: identifier }).strict(),
		functionsRegion: FunctionsRegionSchema,
		// Where the default Storage bucket lives, as Eventarc names it: a region
		// (europe-west3) or a multi-region (eu). A storage trigger deploys there.
		storageBucketLocation: z.string().regex(/^[a-z]+(?:-[a-z]+\d)?$/),
		// The Hosting site venue custom domains attach to: the project's
		// `-venues` site when it has one, else the default site. Written by
		// discovery, so a custom main hostname never moves it.
		venueSiteId: z.string().regex(/^[a-z0-9-]+$/),
		// justifies: scanner releases bind to the Hosting site observed by its role.
		scannerSiteId: z
			.string()
			.regex(/^[a-z0-9-]+$/)
			.nullish(),
		// Whether this deployment must hold a signed licence (managed deployments
		// do); the deployment environment's LICENCE_REQUIRED.
		licenceRequired: z.boolean(),
	})
	.strict();
export type BrowserBootstrap = z.infer<typeof BrowserBootstrapSchema>;

// Server-only: discovery's runtime.json, read by the functions bundle beside
// itself and never published to a browser.
export const FunctionRuntimeSchema = z
	.object({
		// justifies: bare-host requests open this organization when explicitly configured.
		defaultOrganizationId: DefaultOrganizationIdSchema.optional(),
		// justifies: the three checkout functions keep their deployed warm floor.
		checkoutMinInstances: z
			.object({
				preparePayment: z.number().int().min(0).optional(),
				paymentWebhook: z.number().int().min(0).optional(),
				settleOrder: z.number().int().min(0).optional(),
			})
			.strict()
			.default({}),
		// justifies: setGlobalOptions({ serviceAccount }) for every function.
		// Written only when the project holds this dedicated account; absent,
		// every function runs as the platform's default compute account.
		runtimeServiceAccount: z
			.string()
			.regex(
				/^functions-runtime@[a-z][a-z0-9-]{4,28}[a-z0-9]\.iam\.gserviceaccount\.com$/,
			)
			.nullish(),
	})
	.strict();
export type FunctionRuntime = z.infer<typeof FunctionRuntimeSchema>;
